import { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HimmaContext } from '../Context/HimmaContext';
import { PublicSettingsResponse } from '../client-sdk/api';

export const usePublicSettings = () => {
  const api = useContext(HimmaContext)!;
  const { i18n } = useTranslation();
  const [settings, setSettings] = useState<PublicSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPublicSettings = useCallback(
    async (lang?: 'en' | 'ar') => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getPublicSettings(lang || (i18n.language === 'ar' ? 'ar' : 'en'));
        setSettings(response.data);
        return response.data;
      } catch (err) {
        let errorMessage = 'Failed to fetch public settings';
        
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
          if (axiosError.response?.data?.message) {
            errorMessage = axiosError.response.data.message;
          } else if (axiosError.message) {
            errorMessage = axiosError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        console.error('Error fetching public settings:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, i18n.language]
  );

  useEffect(() => {
    fetchPublicSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when language changes
  useEffect(() => {
    if (settings) {
      fetchPublicSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return {
    settings,
    loading,
    error,
    refetch: fetchPublicSettings,
    featuredRestaurantEnabled: settings?.general?.featuredRestaurantEnabled ?? false,
  };
};

